/**
 * Vector Clock implementation for MobyNet
 * 
 * This provides a logical clock system to ensure proper message ordering
 * and synchronization between peers, especially important for file transfers.
 */

/**
 * A vector clock is a map from node IDs to logical timestamps
 */
export type VectorClock = Map<string, number>;

/**
 * Create a new vector clock
 * @param nodeId The ID of the local node
 * @returns A new vector clock with the local node's timestamp set to 1
 */
export function createVectorClock(nodeId: string): VectorClock {
  const clock = new Map<string, number>();
  clock.set(nodeId, 1);
  return clock;
}

/**
 * Create a copy of a vector clock
 * @param clock The vector clock to copy
 * @returns A new vector clock with the same values
 */
export function copyVectorClock(clock: VectorClock): VectorClock {
  return new Map(clock);
}

/**
 * Increment the local node's timestamp in a vector clock
 * @param clock The vector clock to update
 * @param nodeId The ID of the local node
 * @returns The updated vector clock
 */
export function incrementVectorClock(clock: VectorClock, nodeId: string): VectorClock {
  const newClock = copyVectorClock(clock);
  const current = newClock.get(nodeId) || 0;
  newClock.set(nodeId, current + 1);
  return newClock;
}

/**
 * Merge two vector clocks, taking the maximum value for each node
 * @param clockA The first vector clock
 * @param clockB The second vector clock
 * @returns A new vector clock with the merged values
 */
export function mergeVectorClocks(clockA: VectorClock, clockB: VectorClock): VectorClock {
  const merged = copyVectorClock(clockA);
  
  for (const [nodeId, timestamp] of clockB.entries()) {
    const currentTimestamp = merged.get(nodeId) || 0;
    merged.set(nodeId, Math.max(currentTimestamp, timestamp));
  }
  
  return merged;
}

/**
 * Compare two vector clocks to determine their relationship
 * @param clockA The first vector clock
 * @param clockB The second vector clock
 * @returns -1 if clockA < clockB, 0 if they are concurrent, 1 if clockA > clockB
 */
export function compareVectorClocks(clockA: VectorClock, clockB: VectorClock): -1 | 0 | 1 {
  let aLessThanB = false;
  let bLessThanA = false;
  
  // Get all unique node IDs from both clocks
  const nodeIds = new Set<string>();
  for (const nodeId of clockA.keys()) nodeIds.add(nodeId);
  for (const nodeId of clockB.keys()) nodeIds.add(nodeId);
  
  // Compare timestamps for each node
  for (const nodeId of nodeIds) {
    const timestampA = clockA.get(nodeId) || 0;
    const timestampB = clockB.get(nodeId) || 0;
    
    if (timestampA < timestampB) {
      aLessThanB = true;
    } else if (timestampA > timestampB) {
      bLessThanA = true;
    }
  }
  
  // Determine relationship
  if (aLessThanB && !bLessThanA) {
    return -1; // clockA < clockB (clockA happened before clockB)
  } else if (!aLessThanB && bLessThanA) {
    return 1;  // clockA > clockB (clockA happened after clockB)
  } else {
    return 0;  // Concurrent or identical
  }
}

/**
 * Serialize a vector clock to a string for storage or transmission
 * @param clock The vector clock to serialize
 * @returns A JSON string representation of the vector clock
 */
export function serializeVectorClock(clock: VectorClock): string {
  const obj: Record<string, number> = {};
  for (const [nodeId, timestamp] of clock.entries()) {
    obj[nodeId] = timestamp;
  }
  return JSON.stringify(obj);
}

/**
 * Deserialize a vector clock from a string
 * @param serialized The serialized vector clock
 * @returns The deserialized vector clock
 */
export function deserializeVectorClock(serialized: string): VectorClock {
  const obj = JSON.parse(serialized);
  const clock = new Map<string, number>();
  
  for (const nodeId in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, nodeId)) {
      clock.set(nodeId, obj[nodeId]);
    }
  }
  
  return clock;
}

/**
 * Checks if an event with the given vector clock should be processed based on the current vector clock
 * @param currentClock The current vector clock
 * @param eventClock The vector clock of the incoming event
 * @returns true if the event should be processed, false if it's out of order
 */
export function shouldProcessEvent(currentClock: VectorClock, eventClock: VectorClock): boolean {
  const comparison = compareVectorClocks(currentClock, eventClock);
  
  // If event happened before or is concurrent with current state, process it
  return comparison <= 0;
}
